import React, { useEffect, useRef, useState } from 'react'

import { useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import { useModalContext } from 'context/modalContext'

type QueryKey = (string | number | boolean | Record<string, any> | undefined | null)[]

interface Props {
  children: React.ReactNode
  scrollContainerRef: React.RefObject<HTMLElement>
  invalidateQueries: Array<QueryKey | (() => Promise<any> | any)>
}

const CustomPullToRefresh: React.FC<Props> = ({
  children,
  scrollContainerRef,
  invalidateQueries,
}) => {
  const queryClient = useQueryClient()
  const { isOpen, isSecondaryOpen } = useModalContext()
  const modalOpen = isOpen || isSecondaryOpen
  const innerRef = useRef<HTMLDivElement>(null)

  const [refreshing, setRefreshing] = useState(false)
  const pullRef = useRef(0)
  const [, setPullDistance] = useState(0)

  // gesture state
  const startYRef = useRef<number | null>(null)
  const startXRef = useRef<number | null>(null)
  const armedRef = useRef(false)
  const scrollElRef = useRef<HTMLElement | null>(null)
  const topStableCountRef = useRef(0) // require >= 2 consecutive "atTop" moves to arm

  const MAX_PULL = 18
  const TRIGGER_THRESHOLD = 18
  const ARM_THRESHOLD = 3
  const TOP_TOLERANCE = 5
  const REFRESH_COOLDOWN_MS = 8000 // 8 seconds

  const dampen = (d: number) => Math.min(MAX_PULL, d * 0.5)
  const setDistance = (val: number) => {
    pullRef.current = val
    setPullDistance(val)
  }

  const resetGesture = () => {
    startYRef.current = null
    startXRef.current = null
    armedRef.current = false
    scrollElRef.current = null
    topStableCountRef.current = 0
  }

  const refreshLockRef = useRef(false)
  const lastRefreshTimeRef = useRef(0)


  const isIgnoredTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return !!target.closest('.stats__overlay, .stats__slider, .stats__data')
  }

  const isScrollable = (el: HTMLElement | null) => {
    if (!el) return false
    const style = window.getComputedStyle(el)
    const oy = style.overflowY
    return (oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight
  }

  // Find the actually scrolling element starting from the touch target
const findScrollable = (start: Element | null): HTMLElement => {
    let el = start as HTMLElement | null

    while (el && el !== document.body && el !== document.documentElement) {
        const style = window.getComputedStyle(el)
        const overflowY = style.overflowY
        const canScroll =
            (overflowY === 'auto' || overflowY === 'scroll') &&
            el.scrollHeight > el.clientHeight

        if (canScroll) {
            return el
        }

        el = el.parentElement
    }

    return document.scrollingElement as HTMLElement
}

  const atTop = (el: HTMLElement) => el.scrollTop <= TOP_TOLERANCE
  const atBottom = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight <= 0

  const handleTouchStart = (e: TouchEvent) => {
    if (isIgnoredTarget(e.target)) return
    const t = e.touches[0]
    startYRef.current = t.clientY
    startXRef.current = t.clientX
    scrollElRef.current = findScrollable(e.target as Element)
    armedRef.current = false
    topStableCountRef.current = 0
  }

  const handleTouchMove = (e: TouchEvent) => {
    const sy = startYRef.current
    const sx = startXRef.current
    const scrollEl = scrollElRef.current
    if (sy == null || sx == null || !scrollEl) return

    const t = e.touches[0]
    const dy = t.clientY - sy
    const dx = t.clientX - sx

    // Horizontal swipe -> not PTR
    if (Math.abs(dx) > Math.abs(dy)) {
      resetGesture()
      setDistance(0)
      return
    }

    // Never arm at the bottom (avoid rubber-band)
    if (atBottom(scrollEl)) {
      resetGesture()
      setDistance(0)
      return
    }

    // Debounce top detection: require 2 consecutive moves with atTop === true
    if (atTop(scrollEl)) {
      topStableCountRef.current = Math.min(1, topStableCountRef.current + 1)
    } else {
      topStableCountRef.current = 0
    }

    // Only arm when user is pulling down, at top stably, and passed threshold
    if (!armedRef.current) {
      if (dy > ARM_THRESHOLD && topStableCountRef.current >= 1) {
        armedRef.current = true
      } else {
        return // not armed: let native scroll continue
      }
    }

    // Once armed: only allow down; leaving top cancels
    if (dy <= 0 || !atTop(scrollEl)) {
      resetGesture()
      setDistance(0)
      return
    }

    if (e.cancelable) e.preventDefault() // block native scroll only after arming
    setDistance(dampen(dy))
  }

const handleTouchEnd = () => {
  const current = pullRef.current

  if (!armedRef.current) {
    setDistance(0)
    resetGesture()
    return
  }

  const shouldRefresh = current >= TRIGGER_THRESHOLD
  resetGesture()

  if (!shouldRefresh) {
    setDistance(0)
    return
  }

  // Proceed with pull-to-refresh motion
  setRefreshing(true)

  // --- prevent overlap ---
  if (refreshLockRef.current) {
    // still let animation play, but don't re-run queries
    setTimeout(() => {
      setRefreshing(false)
      setDistance(0)
    }, 300)
    return
  }

  // --- cooldown guard ---
  const now = Date.now()
  if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN_MS) {
    // skip re-fetch, just finish animation
    setTimeout(() => {
      setRefreshing(false)
      setDistance(0)
    }, 300)
    return
  }

  // --- legit refresh ---
  refreshLockRef.current = true
  lastRefreshTimeRef.current = now

  const promises = invalidateQueries.map(async (item) => {
    if (typeof item === 'function') {
      return await Promise.resolve(item())
    } else {
      return await queryClient.invalidateQueries(item)
    }
  })

  Promise.allSettled(promises).finally(() => {
    setTimeout(() => {
      setRefreshing(false)
      setDistance(0)
      refreshLockRef.current = false
    }, 300)
  })
}
useEffect(() => {
  // Never allow pull-to-refresh while any context modal is open.
  if (modalOpen) {
    resetGesture()
    setDistance(0)
    return
  }

  const el =
    scrollContainerRef.current ??
    (document.scrollingElement as HTMLElement) ??
    document.documentElement

  if (!el) return

  const opts: AddEventListenerOptions = { passive: false }

  el.addEventListener('touchstart', handleTouchStart, opts)
  el.addEventListener('touchmove', handleTouchMove, opts)
  el.addEventListener('touchend', handleTouchEnd)

  return () => {
    el.removeEventListener('touchstart', handleTouchStart)
    el.removeEventListener('touchmove', handleTouchMove)
    el.removeEventListener('touchend', handleTouchEnd)

    resetGesture()
    setDistance(0)
  }
}, [modalOpen, scrollContainerRef])

  return (
    <div
      ref={innerRef}
      style={{
        transform: `translateY(${refreshing ? 0 : pullRef.current}px)`,
        transition: 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)',
      }}
    >
      {refreshing && (
        <div style={spinnerOverlayStyle}>
          <BeatLoader color="#000000" loading={true} size={8} />
        </div>
      )}
      {children}
    </div>
  )
}

export default CustomPullToRefresh

const spinnerOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 888,
  pointerEvents: 'none',
  marginTop: '10px',
}
